import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useMemo } from 'react';
import { Switch as MuiSwitch } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import colors from '../../foundations/colors.module.scss';
import { Checkmark, Minus } from '../../icons';
import { useIntentBehavior } from '../../providers/IntentBehavior';
import { getTestIds } from '../../utils/TestIds';
import { useBiAction } from '../BiTracking';
import { CircularLoader } from '../CircularLoader';
import { ConditionalWrapper } from '../ConditionalWrapper';
import { Tooltip } from '../Tooltip';
export const GetSwitchTID = (parentTestId)=>getTestIds({
        parentTestId,
        deprecatedTestIds: {
            SwitchTID: {
                Input: parentTestId ? "".concat(parentTestId, "-input") : undefined
            }
        }
    });
export const SWITCH_WIDTH = 48;
const useStyles = makeStyles({
    root: {
        width: SWITCH_WIDTH,
        height: 24,
        padding: 0
    },
    switchBase: {
        padding: 1,
        color: colors.white,
        '&.Mui-focusVisible:not($disabled) + $track': {
            boxShadow: "0 0 0 2px ".concat(colors.white, ", 0 0 0 4px ").concat(colors.purple800)
        },
        '&$checked': {
            color: colors.white,
            transform: 'translateX(24px)',
            '& + $track': {
                backgroundColor: colors.buttonPrimary,
                opacity: 1,
                border: 'none'
            },
            '&:hover + $track': {
                backgroundColor: colors.buttonPrimaryDark
            },
            '& $customThumb': {
                color: colors.buttonPrimary
            },
            '&$disabled $customThumb': {
                color: colors.purple400
            },
            '&$disabled + $track': {
                backgroundColor: colors.purple400
            },
            '&$disabled:hover + $track': {
                backgroundColor: colors.purple500
            },
            '&$loading + $track, &$loading:hover + $track': {
                backgroundColor: colors.purple300
            }
        },
        '&$disabled $customThumb': {
            color: colors.dark300
        },
        '&:hover + $track': {
            backgroundColor: colors.buttonDisabledDark
        },
        '&:hover $customThumb': {
            color: colors.buttonDisabledDark
        },
        '&$loading + $track, &$loading:hover + $track': {
            backgroundColor: colors.dark300
        }
    },
    checked: {},
    disabled: {},
    loading: {},
    track: {
        borderRadius: 90,
        backgroundColor: colors.buttonDisabled,
        opacity: 1
    },
    customThumb: {
        boxSizing: 'border-box',
        width: 22,
        height: 22,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        background: colors.white,
        color: colors.buttonDisabled,
        borderRadius: '50%'
    },
    wrapper: {
        display: 'inline-flex'
    }
});
export function Switch(props) {
    const { className, tooltip, intent, disabled, loading, testId, checked, bi, noBi: _noBi } = props, rest = _object_without_properties(props, [
        "className",
        "tooltip",
        "intent",
        "disabled",
        "loading",
        "testId",
        "checked",
        "bi",
        "noBi"
    ]);
    const styles = useStyles();
    const { customThumb, wrapper } = styles;
    const switchClasses = useMemo(()=>{
        const { loading: lc, customThumb: _ct, wrapper: _w } = styles, rs = _object_without_properties(styles, [
            "loading",
            "customThumb",
            "wrapper"
        ]);
        return _object_spread_props(_object_spread({}, rs), {
            switchBase: cn(rs.switchBase, loading && lc)
        });
    }, [
        styles,
        loading
    ]);
    const { SwitchTID } = useMemo(()=>GetSwitchTID(testId), [
        testId
    ]);
    const { getDisabled, getTooltip } = useIntentBehavior();
    const disabledFromIntent = intent ? getDisabled(intent) : false;
    const tooltipFromIntent = intent ? getTooltip(intent) : null;
    const hasTooltip = !!(tooltip === null || tooltip === void 0 ? void 0 : tooltip.title) || !!tooltipFromIntent;
    const isDisabled = disabledFromIntent || disabled;
    const logBiAction = useBiAction();
    const handleChange = useCallback((event, newChecked)=>{
        var _rest_onChange;
        if (loading) {
            return;
        }
        if (bi === null || bi === void 0 ? void 0 : bi.name) {
            logBiAction({
                entityType: 'switch',
                entityName: bi.name,
                actionType: newChecked ? 'on' : 'off',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: testId !== null && testId !== void 0 ? testId : ''
                })
            });
        }
        (_rest_onChange = rest.onChange) === null || _rest_onChange === void 0 ? void 0 : _rest_onChange.call(rest, event, newChecked);
    }, [
        loading,
        bi,
        logBiAction,
        rest.onChange,
        testId
    ]);
    const loaderNode = /*#__PURE__*/ _jsx(CircularLoader, {
        size: 16,
        testId: SwitchTID.Loader()
    });
    const thumbContent = loading ? loaderNode : /*#__PURE__*/ _jsx(Minus, {
        size: 16
    });
    const checkedThumbContent = loading ? loaderNode : /*#__PURE__*/ _jsx(Checkmark, {
        size: 16
    });
    return /*#__PURE__*/ _jsx(ConditionalWrapper, {
        condition: hasTooltip,
        wrapper: (children)=>/*#__PURE__*/ _jsx(Tooltip, _object_spread_props(_object_spread({}, tooltip || {
                placement: 'top',
                title: tooltipFromIntent
            }), {
                children: /*#__PURE__*/ _jsx("div", {
                    className: cn(wrapper, className),
                    "data-testid": SwitchTID.Wrapper(),
                    children: children
                })
            })),
        children: /*#__PURE__*/ _jsx(MuiSwitch, _object_spread_props(_object_spread({}, rest), {
            onChange: handleChange,
            checked: checked,
            "aria-checked": checked,
            disabled: isDisabled,
            "data-testid": testId,
            className: cn(!hasTooltip && className),
            classes: switchClasses,
            disableRipple: true,
            icon: /*#__PURE__*/ _jsx("div", {
                className: customThumb,
                children: thumbContent
            }),
            checkedIcon: /*#__PURE__*/ _jsx("div", {
                className: customThumb,
                children: checkedThumbContent
            }),
            inputProps: _object_spread_props(_object_spread({}, rest.inputProps), {
                'data-testid': SwitchTID.Input(),
                'aria-busy': !!loading
            })
        }))
    });
}
